import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { Storage } from '@targx/libs/storage';
import { TenantService } from '@core/tenant/tenant.service';
import {
  ConsentCategory,
  ConsentState,
  CONSENT_STORAGE_KEY,
  POLICY_VERSION,
} from './consent.model';

/**
 * Fonte de verdade do consent RGPD (sites dealer). SSR-aware: no servidor nunca
 * há decisão (banner não renderiza). Persiste via Storage (localStorage encriptado)
 * e regista prova no backend (consent-log). O TenantService reage a `state$` para
 * injectar scripts só depois do opt-in (prior-blocking).
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly _isBrowser: boolean;
  private readonly _state$: BehaviorSubject<ConsentState | null>;
  private readonly _open$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private _http: HttpClient,
    private _injector: Injector, // lazy TenantService — evita DI circular
  ) {
    this._isBrowser = isPlatformBrowser(platformId);
    this._state$ = new BehaviorSubject<ConsentState | null>(this._load());
  }

  // ---- reads ----
  get state$(): Observable<ConsentState | null> {
    return this._state$.asObservable();
  }
  get openSubject(): Observable<void> {
    return this._open$.asObservable();
  }
  current(): ConsentState | null {
    return this._state$.value;
  }
  hasDecided(): boolean {
    return this._state$.value !== null;
  }

  isAccepted(cat: ConsentCategory): boolean {
    if (cat === 'necessary') return true;
    return this._state$.value?.[cat] === true;
  }

  // ---- writes ----
  acceptAll(): void {
    this._decide(true, true);
  }
  rejectAll(): void {
    this._decide(false, false);
  }
  accept(categories: Partial<Record<ConsentCategory, boolean>>): void {
    this._decide(!!categories.analytics, !!categories.marketing);
  }

  /** Reabre a barra (link "Gerir cookies"). */
  open(): void {
    this._open$.next();
  }

  // ---- internals ----
  private _decide(analytics: boolean, marketing: boolean): void {
    const state: ConsentState = {
      necessary: true,
      analytics,
      marketing,
      version: POLICY_VERSION,
      timestamp: new Date().toISOString(),
    };
    this._persist(state);
    this._state$.next(state);
    this._logProof(state);
  }

  private _persist(state: ConsentState): void {
    if (!this._isBrowser) return;
    try {
      Storage.set(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* localStorage indisponível — decisão fica só em memória nesta sessão */
    }
  }

  private _load(): ConsentState | null {
    if (!this._isBrowser) return null;
    try {
      const raw = Storage.get(CONSENT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ConsentState;
      // Policy mudou → re-prompt
      if (parsed?.version !== POLICY_VERSION) return null;
      return { ...parsed, necessary: true };
    } catch {
      return null;
    }
  }

  private _logProof(state: ConsentState): void {
    if (!this._isBrowser) return;
    let sellerId: number | null = null;
    try {
      const tenant = this._injector.get(TenantService);
      const id = tenant.profile?.id;
      sellerId = id != null ? Number(id) : null;
    } catch {
      sellerId = null;
    }
    this._http
      .post(`${environment.api}/consent-logs/log`, {
        data: {
          choices: {
            necessary: true,
            analytics: state.analytics,
            marketing: state.marketing,
          },
          policyVersion: state.version,
          sellerId,
        },
      })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }
}
